import { collection, addDoc, getDocs, getDoc, setDoc, doc, query, where, Timestamp } from "firebase/firestore";
import { db, auth } from "./firebaseConfig";

// ── Workout History ──────────────────────────────────────────

export const saveCompletedDeck = async (deckType) => {
  try {
    const user = auth.currentUser;
    if (!user) return;
    await addDoc(collection(db, "completedDecks"), {
      deckType,
      completedAt: Timestamp.now(),
      userId: user.uid
    });
  } catch (error) {
    console.error("Error saving completed deck:", error);
  }
};

export const getTotalCompletedDecks = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return 0;
    const q = query(collection(db, "completedDecks"), where("userId", "==", user.uid));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error("Error fetching deck count:", error);
    return 0;
  }
};

export const getWorkoutHistory = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return [];
    const q = query(collection(db, "completedDecks"), where("userId", "==", user.uid));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => doc.data())
      .filter(entry => entry.completedAt)
      .sort((a, b) => b.completedAt.toDate() - a.completedAt.toDate());
  } catch (error) {
    console.error("Error fetching workout history:", error);
    return [];
  }
};

// ── Goals ────────────────────────────────────────────────────

export const saveGoal = async (goal) => {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    const docRef = await addDoc(collection(db, "goals"), {
      ...goal,
      userId: user.uid,
      createdAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error("Error saving goal:", error);
    return null;
  }
};

export const getGoals = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return [];
    const q = query(collection(db, "goals"), where("userId", "==", user.uid));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());
  } catch (error) {
    console.error("Error fetching goals:", error);
    return [];
  }
};

export const deleteGoal = async (goalId) => {
  try {
    const { deleteDoc } = await import("firebase/firestore");
    await deleteDoc(doc(db, "goals", goalId));
  } catch (error) {
    console.error("Error deleting goal:", error);
  }
};

// ── Journal Entries ──────────────────────────────────────────

export const saveJournalEntry = async (goalId, text) => {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    const docRef = await addDoc(collection(db, "journalEntries"), {
      goalId,
      text,
      userId: user.uid,
      createdAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error("Error saving journal entry:", error);
    return null;
  }
};

export const getJournalEntries = async (goalId) => {
  try {
    const user = auth.currentUser;
    if (!user) return [];
    const q = query(
      collection(db, "journalEntries"),
      where("userId", "==", user.uid),
      where("goalId", "==", goalId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());
  } catch (error) {
    console.error("Error fetching journal entries:", error);
    return [];
  }
};

// ── Premium Access ───────────────────────────────────────────

export const checkPremiumAccess = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return false;
    const docRef = doc(db, "premiumUsers", user.uid);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  } catch (error) {
    console.error("Error checking premium access:", error);
    return false;
  }
};

export const unlockPremiumAccess = async (code) => {
  try {
    const user = auth.currentUser;
    if (!user) return false;
    if (code.toLowerCase().trim() !== 'grind365') return false;
    await setDoc(doc(db, "premiumUsers", user.uid), {
      userId: user.uid,
      codeUsed: code,
      unlockedAt: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error("Error unlocking premium:", error);
    return false;
  }
};

// ── Matches ──────────────────────────────────────────────────

export const getMatches = async () => {
  try {
    const snapshot = await getDocs(collection(db, "matches"));
    return snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => b.dateAdded?.toDate() - a.dateAdded?.toDate());
  } catch (error) {
    console.error("Error fetching matches:", error);
    return [];
  }
};

// ── Fundraising ──────────────────────────────────────────────

export const getFundraisingData = async () => {
  try {
    const snapshot = await getDocs(collection(db, "fundraising"));
    if (snapshot.empty) return { amountRaised: 0, goal: 2000, legacyGoal: 2500 };
    return snapshot.docs[0].data();
  } catch (error) {
    console.error("Error fetching fundraising data:", error);
    return { amountRaised: 0, goal: 2000, legacyGoal: 2500 };
  }
};